import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { fetchData } from 'utils';
import { useClientConstraintsQuery } from 'hooks/webapi/application/useUpsale';
import {
  useAffiliate,
  useClientQuery,
  useExternalConsentsDataQuery,
  usePrecheckApplicationMutation,
} from 'hooks/webapi';
import useCalculator from 'hooks/calculator/useCalculator';
import { WAITING } from 'Router/routes';
import {
  CreateApplicationOptions,
  useCreateApplicationWithoutRiskAndSendGACookiesMutation,
} from 'hooks/webapi/client/useCreateApplicationMutation';
import useIovation from 'hooks/useIovation';

export default function useWelcomeBackNewApplication() {
  const navigate = useNavigate();
  const { data: constraints } = useClientConstraintsQuery();

  const { refetch: fetchClient, data: client } = useClientQuery();
  const { amount, handleAmountChange, offer, loading } = useCalculator();
  const { mutateAsync: createClientApplicationWithoutRisk } =
    useCreateApplicationWithoutRiskAndSendGACookiesMutation();
  const { affiliate, removeAffiliateData } = useAffiliate();
  const [pending, setPending] = useState(false);
  const { ioBlackBox } = useIovation();
  const { mutateAsync: precheckApplication } = usePrecheckApplicationMutation();
  const { data: consents, isLoading: consentsLoading } =
    useExternalConsentsDataQuery();

  useEffect(() => {
    if (!client) {
      fetchClient();
    }

    if (!constraints) {
      return;
    }

    handleAmountChange(constraints.amountInterval.max);
  }, [constraints, handleAmountChange, client]);

  const consentTelco = consents?.UNDERSTANDING_AGREEMENT_CONFIRMED;
  const consent = consentTelco?.content[0].text;

  const acceptTelcoConsent = async () => {
    const consentPayload = {
      consents: [
        {
          name: 'UNDERSTANDING_AGREEMENT_CONFIRMED',
          accepted: true,
          version: consentTelco?.version,
        },
      ],
    };
    await fetchData('/client/settings', {
      method: 'put',
      body: JSON.stringify(consentPayload),
    });
  };

  const handleConfirm = useCallback(async () => {
    if (!offer) {
      return;
    }

    const applicationOptions: CreateApplicationOptions = {
      amount: offer.newPrincipal,
      term: offer.term,
      type: 'WEB',
      source: 'DESKTOP',
      affiliateInfo: affiliate,
      ioBlackBox,
    };
    try {
      setPending(true);

      await acceptTelcoConsent();
      await createClientApplicationWithoutRisk(applicationOptions);
      await precheckApplication();

      if (affiliate) {
        removeAffiliateData();
      }

      navigate(WAITING);
    } catch {
      // Empty catch
    } finally {
      setPending(false);
    }
  }, [
    affiliate,
    createClientApplicationWithoutRisk,
    ioBlackBox,
    offer,
    precheckApplication,
    removeAffiliateData,
    consentTelco,
  ]);

  return {
    constraints,
    amount,
    loading,
    pending,
    offer,
    handleConfirm,
    handleAmountChange,
    client,
    consent,
    consentsLoading,
  };
}
